import {
  type RefObject,
  type FormEvent,
  type KeyboardEvent,
  type ChangeEvent,
  type ClipboardEvent,
  type PointerEvent as ReactPointerEvent,
  useRef,
  useCallback,
  useLayoutEffect,
  lazy,
  Suspense,
  useDeferredValue,
  useState,
  useEffect,
} from "react";
import type { Message } from "../hooks/useChat";
import {
  useVoiceRecorder,
  hasMediaRecorder,
} from "../hooks/useVoiceRecorder";
import { X, PenSquare, FileText, Paperclip, Loader2, Send, Mic, Square, Smile, Play, Trash2, Check, Type } from "lucide-react";
import { safeMessageText } from "../lib/safeMessageText";
import styles from "./ChatView.module.css";
import { useMarkdownShortcuts } from "../hooks/useMarkdownShortcuts";
import { MarkdownToolbar } from "./MarkdownToolbar";
import { MessageMarkdown } from "./MessageMarkdown";

const EmojiPicker = lazy(() => import("./EmojiPicker").then(m => ({ default: m.EmojiPicker })));

export interface MessageInputProps {
  channelId: string;
  inputText: string;
  onInputChange: (text: string) => void;
  onSubmit: (e: FormEvent) => void;
  onKeyDown: (e: KeyboardEvent) => void;
  onFileSelect: (e: ChangeEvent<HTMLInputElement>) => void;
  onPaste?: (e: ClipboardEvent) => void;
  onEmojiSelect: (emoji: string) => void;
  showEmojiPicker: boolean;
  onToggleEmojiPicker: () => void;
  uploading: boolean;
  editingMessage: Message | null;
  replyTo: Message | null;
  /** KOL-108: mehrere Dateien je Nachricht. */
  pendingFiles: { file: File; preview: string | null }[];
  /** Fortschritt beim Hochladen mehrerer Dateien, z. B. „Datei 2 von 5…". */
  uploadHinweis?: string | null;
  onCancelFile: (index: number) => void;
  onCancelEdit: () => void;
  onCancelReply: () => void;
  inputRef: RefObject<HTMLTextAreaElement | null>;
  fileInputRef: RefObject<HTMLInputElement | null>;
  onSendVoice: (attachment: string) => void;
}

// Long press removed — voice recording now uses toggle (click start / click stop)

export function MessageInput({
  channelId,
  inputText,
  onInputChange,
  onSubmit,
  onKeyDown,
  onFileSelect,
  onPaste,
  onEmojiSelect,
  showEmojiPicker,
  onToggleEmojiPicker,
  uploading,
  editingMessage,
  replyTo,
  pendingFiles,
  uploadHinweis,
  onCancelFile,
  onCancelEdit,
  onCancelReply,
  inputRef,
  fileInputRef,
  onSendVoice,
}: MessageInputProps) {
  const voice = useVoiceRecorder(channelId);
  const audioPreviewRef = useRef<HTMLAudioElement | null>(null);

  const [previewOn, setPreviewOn] = useState<boolean>(() => {
    try {
      return localStorage.getItem("md-preview") === "1";
    } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("md-preview", previewOn ? "1" : "0"); } catch {}
  }, [previewOn]);
  const [toolbarOn, setToolbarOn] = useState<boolean>(() => {
    try {
      return localStorage.getItem("md-toolbar") !== "0";
    } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("md-toolbar", toolbarOn ? "1" : "0"); } catch {}
  }, [toolbarOn]);
  const [textareaTall, setTextareaTall] = useState<boolean>(false);
  const dragRef = useRef<{ y: number; h: number; active: boolean }>({ y: 0, h: 0, active: false });
  const deferredText = useDeferredValue(inputText);
  useMarkdownShortcuts(inputRef, inputText, onInputChange);

  // Auto-grow + preserve manual resize:
  //   - On every inputText change, compare current rendered height against the
  //     content height (scrollHeight). If content needs more room, expand.
  //   - If the user grabbed the resize handle (current > content), we leave the
  //     height alone — manual size persists across keystrokes until either
  //     content overflows it or the textarea is cleared (post-send).
  //   - Empty input → strip inline height so CSS min-height/rows take over,
  //     collapsing the box back to a single row.
  // CSS max-height caps the auto-grow ceiling; the user's resize handle can
  // still drag past it because resize: vertical wins over max-height.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const before = el.offsetHeight;
    el.style.height = "auto";
    const content = el.scrollHeight;
    if (inputText === "") {
      el.style.height = "";
      el.style.maxHeight = "";
    } else if (content > before) {
      el.style.height = `${content}px`;
    } else {
      el.style.height = `${before}px`;
    }
    setTextareaTall((el.offsetHeight ?? 0) > 90);
  }, [inputText, inputRef]);

  const onResizeDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const el = inputRef.current;
    if (!el) return;
    dragRef.current = { y: e.clientY, h: el.offsetHeight, active: true };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  }, [inputRef]);

  const onResizeMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current.active) return;
    const el = inputRef.current;
    if (!el) return;
    const dy = dragRef.current.y - e.clientY;
    const maxH = Math.max(120, window.innerHeight * 0.6);
    const newH = Math.min(maxH, Math.max(40, dragRef.current.h + dy));
    el.style.height = `${newH}px`;
    el.style.maxHeight = `${newH}px`;
    setTextareaTall(newH > 90);
  }, [inputRef]);

  const onResizeUp = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
  }, []);

  // Toggle: click mic → start recording, click stop → preview
  const handleMicClick = useCallback(async () => {
    if (voice.state === "recording") {
      await voice.stopRecording(); // → preview state
    } else if (voice.state === "idle") {
      if (hasMediaRecorder) {
        voice.startRecording();
      }
    }
  }, [voice]);

  // Send from preview
  const handleSendVoice = useCallback(async () => {
    const attachment = await voice.sendRecording();
    console.log("[voice] sendRecording returned:", attachment);
    if (attachment) {
      try {
        await onSendVoice(attachment);
        console.log("[voice] onSendVoice completed");
      } catch (err) {
        console.error("[voice] onSendVoice failed:", err);
      }
    } else {
      console.warn("[voice] sendRecording returned null — upload failed?");
    }
  }, [voice, onSendVoice]);

  // Play preview
  const handlePlayPreview = useCallback(() => {
    if (audioPreviewRef.current) {
      audioPreviewRef.current.play();
    }
  }, []);

  const handleCancelRecording = useCallback(() => {
    voice.cancelRecording();
  }, [voice]);

  const formatDuration = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const isVoiceActive = voice.state !== "idle";
  const canVoice = hasMediaRecorder;

  return (
    <>
      {/* Reply Preview */}
      {replyTo && (
        <div className={styles.replyPreview}>
          <div className={styles.replyPreviewBar} />
          <div className={styles.replyPreviewContent}>
            <span className={styles.replyPreviewName}>
              {replyTo.senderName ?? "Unbekannt"}
            </span>
            <span className={styles.replyPreviewText}>
              {safeMessageText(replyTo).slice(0, 80)}
            </span>
          </div>
          <button
            className={styles.replyPreviewClose}
            onClick={onCancelReply}
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Edit Indicator */}
      {editingMessage && (
        <div className={styles.editIndicator}>
          <span className={styles.editIndicatorIcon}><PenSquare size={16} /></span>
          <span className={styles.editIndicatorText}>Nachricht bearbeiten</span>
          <button
            className={styles.editIndicatorClose}
            onClick={onCancelEdit}
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Pending File Preview — KOL-108: eine Zeile je Datei, einzeln entfernbar */}
      {pendingFiles.length > 0 && (
        <>
          {pendingFiles.map((datei, index) => (
            <div className={styles.filePreview} key={`${datei.file.name}-${index}`}>
              {datei.preview ? (
                <img src={datei.preview} alt="Vorschau" className={styles.filePreviewImg} />
              ) : (
                <div className={styles.filePreviewDoc}>
                  <span className={styles.fileIcon}><FileText size={16} /></span>
                  <span className={styles.fileName}>{datei.file.name}</span>
                </div>
              )}
              <button
                className={styles.fileCancelBtn}
                onClick={() => onCancelFile(index)}
                aria-label={`${datei.file.name} entfernen`}
              >
                <X size={18} />
              </button>
            </div>
          ))}
          {uploadHinweis && <div className={styles.filePreviewDoc}>{uploadHinweis}</div>}
        </>
      )}

      {/* Recording Indicator */}
      {voice.state === "recording" && (
        <div className={styles.recordingIndicator}>
          <span className={styles.recordingDot} />
          <span className={styles.recordingTimer}>{formatDuration(voice.recordingDuration)}</span>
          <span className={styles.recordingText}>Aufnahme...</span>
          <button
            type="button"
            className={styles.recordingCancel}
            onClick={handleCancelRecording}
            aria-label="Aufnahme abbrechen"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Voice Preview (after recording stopped) */}
      {voice.state === "preview" && voice.previewUrl && (
        <div className={styles.recordingIndicator}>
          <audio ref={audioPreviewRef} src={voice.previewUrl} />
          <button
            type="button"
            className={styles.voicePreviewBtn}
            onClick={handlePlayPreview}
            aria-label="Anhören"
          >
            <Play size={16} />
          </button>
          <span className={styles.recordingTimer}>{formatDuration(voice.recordingDuration)}</span>
          <span className={styles.recordingText}>Sprachnachricht</span>
          <button
            type="button"
            className={styles.recordingCancel}
            onClick={handleCancelRecording}
            aria-label="Verwerfen"
          >
            <Trash2 size={16} />
          </button>
          <button
            type="button"
            className={styles.voiceSendBtn}
            onClick={handleSendVoice}
            aria-label="Senden"
          >
            <Send size={16} />
          </button>
        </div>
      )}

      {/* Uploading Indicator */}
      {voice.state === "uploading" && (
        <div className={styles.recordingIndicator}>
          <Loader2 size={16} className="spin" />
          <span className={styles.recordingText}>Wird hochgeladen...</span>
        </div>
      )}

      {toolbarOn && (
        <MarkdownToolbar
          textareaRef={inputRef}
          value={inputText}
          onChange={onInputChange}
          previewOn={previewOn}
          onTogglePreview={() => setPreviewOn((v) => !v)}
          disabled={uploading || isVoiceActive}
        />
      )}

      <div
        className={styles.resizeHandle}
        onPointerDown={onResizeDown}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeUp}
        onPointerCancel={onResizeUp}
        role="separator"
        aria-label="Textfeld vergrößern"
      />

      {/* Input */}
      <form onSubmit={onSubmit} className={styles.inputArea}>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip"
          onChange={onFileSelect}
        />
        <div className={`${styles.leftIcons} ${textareaTall ? styles.leftIconsGrid : ""}`}>
          <button
            type="button"
            className={styles.attachBtn}
            aria-label="Datei anhängen"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || isVoiceActive}
          >
            <Paperclip size={18} />
          </button>

          {/* Emoji Toggle */}
          <div className={styles.emojiWrapper}>
            <button
              type="button"
              className={`${styles.emojiBtn} ${showEmojiPicker ? styles.emojiBtnActive : ""}`}
              aria-label="Emoji auswählen"
              onClick={onToggleEmojiPicker}
              disabled={uploading || isVoiceActive}
            >
              <Smile size={18} />
            </button>
            {showEmojiPicker && (
              <Suspense fallback={<div style={{ padding: 16 }}>⏳</div>}>
                <EmojiPicker
                  onSelect={onEmojiSelect}
                  onClose={onToggleEmojiPicker}
                />
              </Suspense>
            )}
          </div>

          {/* Mic Button — hidden during preview (send button is in preview bar) */}
          {canVoice && voice.state !== "preview" && (
            <button
              type="button"
              className={`${styles.voiceBtn} ${voice.state === "recording" ? styles.voiceBtnActive : ""}`}
              aria-label={voice.state === "recording" ? "Aufnahme stoppen" : "Sprachnachricht aufnehmen"}
              onClick={handleMicClick}
              disabled={uploading || voice.state === "uploading"}
            >
              {voice.state === "recording" ? <Square size={16} /> : <Mic size={18} />}
            </button>
          )}

          <button
            type="button"
            className={`${styles.toolbarToggleBtn} ${toolbarOn ? styles.toolbarToggleBtnActive : ""}`}
            aria-label={toolbarOn ? "Formatierungsleiste ausblenden" : "Formatierungsleiste einblenden"}
            aria-pressed={toolbarOn}
            onClick={() => setToolbarOn((v) => !v)}
          >
            <Type size={18} />
          </button>
        </div>

        <div className={previewOn ? styles.inputWithPreview : styles.inputNoPreview}>
          <textarea
            ref={inputRef}
            className={styles.input}
            placeholder={
              uploading
                ? "Wird hochgeladen..."
                : voice.state === "recording"
                  ? "Sprechen Sie..."
                  : editingMessage
                    ? "Nachricht bearbeiten..."
                    : "Nachricht schreiben..."
            }
            value={inputText}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            rows={1}
            disabled={uploading || voice.state === "recording" || voice.state === "uploading"}
          />
          {previewOn && (
            <div className={styles.previewPane}>
              {deferredText.trim()
                ? <MessageMarkdown text={deferredText} />
                : <span className={styles.previewPlaceholder}>Vorschau erscheint hier</span>}
            </div>
          )}
        </div>
        <button
          type="submit"
          className={`${styles.sendBtn} ${(inputText.trim() || pendingFiles.length > 0) ? styles.active : ""}`}
          disabled={(!inputText.trim() && pendingFiles.length === 0) || uploading || voice.state === "recording" || voice.state === "uploading"}
          aria-label={editingMessage ? "Speichern" : "Senden"}
        >
          {uploading ? <Loader2 size={18} className="spin" /> : editingMessage ? <Check size={18} /> : <Send size={18} />}
        </button>
      </form>
    </>
  );
}
